// The Status chips on the Requisitions list and the closed-reason filter of archive search (D9: "requisition.list admits
// state_archive IN (live, archived); B9 adds status values archived (and chips filled · cancelled · reopened), archived
// rows out of the default list"; "admin.archive_search gains closed_reason"). His: "Archived records remain
// searchable/reportable and retain Candidate ID relationships." Pure data and functions, so the list host and the test
// share one reading of each chip.

import { CLOSED_REASONS, type ArchiveSearchFilters, type ClosedReason } from "./types";

export type ArchiveChipKey = "archived" | "filled" | "cancelled" | "reopened";

/**
 * Each chip and the `status` it sends to requisition.list. Filled reads both the derived `filled` (B9: only HRIS hire
 * fills) and V1's `closed_filled`; Cancelled is `closed_cancelled`. A reopened requisition is live again, so
 * `reopened` is not a stored status: requisition.list must read it as "live with an approved reopen" (the governance
 * milestone `reopened`); until it does, the chip finds nothing rather than a wrong set (S12 report, debt).
 */
export const ARCHIVE_STATUS_CHIPS: readonly { readonly key: ArchiveChipKey; readonly status: string[] }[] = Object.freeze([
  Object.freeze({ key: "archived" as const, status: ["archived"] }),
  Object.freeze({ key: "filled" as const, status: ["filled", "closed_filled"] }),
  Object.freeze({ key: "cancelled" as const, status: ["closed_cancelled"] }),
  Object.freeze({ key: "reopened" as const, status: ["reopened"] }),
]);

/** The status list a chip sends (a copy, so a caller never mutates the table). */
export function chipStatus(key: ArchiveChipKey): string[] {
  const chip = ARCHIVE_STATUS_CHIPS.find((c) => c.key === key);
  return chip ? [...chip.status] : [];
}

/** The chip whose status set equals the list's current states, else null. */
export function activeChip(states: readonly string[]): ArchiveChipKey | null {
  const now = [...new Set(states)].sort().join(",");
  return ARCHIVE_STATUS_CHIPS.find((c) => [...c.status].sort().join(",") === now)?.key ?? null;
}

/** The requisition.list input a chip produces: the base filters with `status` replaced by the chip's. */
export function listInputFor<T extends object>(key: ArchiveChipKey, base: T): T & { status: string[] } {
  return { ...base, status: chipStatus(key) };
}

/** The `state` query value the list keeps in its hash (comma-joined, as the V1 State filter writes it). */
export function chipStateParam(key: ArchiveChipKey): string {
  return chipStatus(key).join(",");
}

/** A closed reason read from free input: one of his two, or null (never an invented value sent to the server). */
export function closedReasonOf(value: string | null | undefined): ClosedReason | null {
  return CLOSED_REASONS.find((reason) => reason === value) ?? null;
}

/** The archive-search input with `closed_reason` set when chosen, and absent (not null) when not. */
export function withClosedReason<T extends object>(input: T, reason: ClosedReason | null): T & Pick<ArchiveSearchFilters, "closed_reason"> {
  if (!reason) return input as T & Pick<ArchiveSearchFilters, "closed_reason">;
  return { ...input, closed_reason: reason };
}
